import { Outlet, Link, useLocation } from "react-router";
import { FsnThemeProvider, FsnThemeSetting } from "@fusion/components/theme";
import { FsnSidenav } from "@fusion/components/Sidenav";
import { FsnHeader } from "@fusion/components/Header";
import { FsnAppInfo } from "@fusion/components/AppInfo";
import { FsnUser } from "@fusion/components/common";
import { FsnUserInfo } from "@fusion/components/UserInfo";
import { FsnNavItem } from "@fusion/components/common";
import { FsnThemeSwitcher } from "@fusion/components/ThemeSwitcher";
import { Home, AdminPanelSettings, Info, ChatBubble } from "@mui/icons-material";
import { useState, useEffect } from "react";
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import { useLogin, isLoggedIn, getActiveAccount, isUser, hasDeveloperRole } from "../../authConfig";
import { NO_ACCESS_ROLE, ALL_VALID_ROLES, CHATBOT_WEBSITE_NAME } from "../../constants";
import { LoginButton } from "../../components/LoginButton";

const LOCAL_STORAGE_KEY = "fsn-theme-setting";

const Layout = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const location = useLocation();
    const [navConfig, setNavConfig] = useState<FsnNavItem[]>([]);
    const [demoUser, setDemoUser] = useState<FsnUser>({});

    const storedThemeSetting: FsnThemeSetting = (localStorage.getItem(LOCAL_STORAGE_KEY) as FsnThemeSetting) ?? "system";
    const handleThemeSettingChange = (themeSetting: FsnThemeSetting) => {
        localStorage.setItem(LOCAL_STORAGE_KEY, themeSetting);
    };
    const client = useLogin ? useMsal().instance : undefined;

    const getNavItems = () => {
        let navItems: FsnNavItem[] = [
            {
                name: "Home",
                icon: <Home />,
                route: "/"
            },
            {
                name: "Chat",
                icon: <ChatBubble />,
                route: "/chat"
            },
            {
                name: "Info",
                icon: <Info />,
                route: "/info"
            },
            {
                name: "Admin",
                icon: <AdminPanelSettings />,
                route: "/admin"
            }
        ];
        if (useLogin) {
            // SITSRS-4619: Show admin nav only for IT role (developer role)
            if (!hasDeveloperRole(getActiveAccount(client))) {
                navItems = navItems.filter(item => item.name !== "Admin");
            }
            if (!isUser(getActiveAccount(client))) {
                navItems = navItems.filter(item => item.name !== "Chat");
            }
        }
        return navItems;
    };

    const getUserInfo = () => {
        const user: FsnUser = {
            displayName: "Display Name",
            firstName: "Firstname",
            lastName: "Lastname",
            gmin: "0000004",
            email: "firstname.lastname@gm.com",
            roles: ["Admin"]
        };
        if (isLoggedIn(client)) {
            const id_claims = client ? JSON.stringify(getActiveAccount(client)?.idTokenClaims ?? {}) : undefined;
            if (id_claims) {
                const userInfo = JSON.parse(id_claims);
                let userRoles = !isUser(getActiveAccount(client)) ? [NO_ACCESS_ROLE] : userInfo.roles;
                userRoles = userRoles.filter((role: string) => ALL_VALID_ROLES.includes(role) || role === NO_ACCESS_ROLE);
                user.displayName = userInfo.name ?? "Display Name";
                user.firstName = userInfo.givenName ?? "Firstname";
                user.lastName = userInfo.sn ?? "Lastname";
                user.email = userInfo.mail ?? "firstname.lastname@gm.com";
                user.roles = userRoles;
                user.gmin = userInfo.gmuniqueidentifier ?? "0000004";
            }
        }
        return user;
    };

    useEffect(() => {
        setDemoUser(getUserInfo());
        setNavConfig(getNavItems());
    }, [isLoggedIn(client)]);

    const appInfo = {
        appName: CHATBOT_WEBSITE_NAME,
        // This is the version that is displayed in the toolbar
        //1.0.0 July 2024
        //1.0.1 some time later?
        //1.1.0 March 2025
        //1.1.1 March 2025 security vulnerability hotfix
        //1.2.0 May 2025
        //1.3.0 September 2025 - Slack integration
        //2.0.0 October 2025 - MCP server & ECR Agent integration
        //2.0.1 January 2026 - Security hotfix
        //2.0.2 March 2026 - Major security-a-thon work for possible security weaknesses identified by new AI tools
        //2.0.3 May 2026 - minor upgrades to ECR agent
        //2.1.0 May 2026 - RAG feature deprecated (Document Q&A moved to Glean)
        //2.2.0 June 2026 - rebranded the app to GPSC Aura
        //2.3.0 July 2026 - rewrote the info page + additional ECR functionality
        version: "2.3.0"
        //TODO: find a way to automatically update the version
        //  either 1.2.0 > 1.2.1 or somehow using the deployment date
        // maybe a database value?

        // environment: "DEV", TODO: consider displaying this in the toolbar automatically
    };

    return (
        <div className="App">
            <AuthenticatedTemplate>
                <FsnThemeProvider themeSetting={storedThemeSetting}>
                    <FsnHeader position="fixed" title={CHATBOT_WEBSITE_NAME} menuVisible={isLoggedIn(client)} menuOnClick={() => setMenuOpen(!menuOpen)}>
                        <FsnAppInfo {...appInfo} />
                        <FsnThemeSwitcher onChange={handleThemeSettingChange} />
                        {isLoggedIn(client) && <FsnUserInfo user={demoUser} />}
                        {useLogin ? <LoginButton /> : <FsnUserInfo user={demoUser} />}
                    </FsnHeader>
                    <FsnSidenav expanded={menuOpen} navItems={navConfig} activeRoute={location.pathname} linkComponent={Link}>
                        <Outlet />
                    </FsnSidenav>
                </FsnThemeProvider>
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
                <div>Signing in...</div>
            </UnauthenticatedTemplate>
        </div>
    );
};

export default Layout;
